import { Box, CircularProgress, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildOrderData } from '../../helpers/tableUtil';
import { SortDirection } from '../../models/global';
import { State } from '../../models/state';
import { getAllRegions, getCountries } from '../../store/actions/country';
import Button from '../FormComponents/Button';
import Dropdown from '../FormComponents/Dropdown';
import Table from '../Table/Table';
import ToastMessage from '../ToastMessage/ToastMessage';

const tableMap = new Map<string, string>([
  ['Name', 'name'],
  ['Region', 'region'],
]);
const columns = Array.from(tableMap.keys());

function Countries() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const rowsPerPage = 10;
  const [page, setPage] = useState(0);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [toast, setToast] = useState('');
  const [pageLoading, setPageLoading] = useState(false);

  const [orderBy, setOrderBy] = useState('Name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [regionFilter, setRegionFilter] = useState<string>('');

  const { countries, countryCount } = useSelector((state: State) => state.country);
  const { regions: regionFilters } = useSelector((state: State) => state.country.simpleFilters);

  useEffect(() => {
    if (!regionFilters.length) {
      dispatch(getAllRegions());
    }
  }, [dispatch, regionFilters.length]);

  useEffect(() => {
    const runOnce = () => {
      setToastType('success');
      setToast(location?.state?.message);
      window.history.replaceState({}, '');
    };
    if (location?.state?.message) {
      runOnce();
    }
  }, [location?.state?.message]);

  useEffect(() => {
    const orderData = buildOrderData({
      orderBy,
      sortDirection,
      tableMap,
    });

    (() => setPageLoading(true))();
    dispatch(
      getCountries(
        {
          limit: rowsPerPage,
          page: page + 1,
          orderData,
          regionFilter,
        },
        () => setPageLoading(false),
      ),
    );
  }, [dispatch, page, orderBy, sortDirection, regionFilter]);

  const goToCreateCountry = () => {
    navigate('/countries/create');
  };

  if (pageLoading) {
    return (
      <Box className='loader'>
        <CircularProgress size={40} color='inherit' />
      </Box>
    );
  }
  return (
    <>
      <Box className='list'>
        <Box className='list-header' sx={{ mb: 2 }}>
          <Typography variant='h5'>Countries</Typography>
          <Box className='create-button'>
            <Button text='Create' onClick={goToCreateCountry} />
          </Box>
        </Box>

        <Box className='simple-filters'>
          <Dropdown
            value={regionFilter}
            label='Region'
            options={regionFilters}
            onChange={(_, v: string) => setRegionFilter(v)}
          />
        </Box>

        <Table
          columns={columns}
          rows={countries}
          count={countryCount}
          page={page}
          rowsPerPage={rowsPerPage}
          setPage={setPage}
          rowClickHandler={(id: string) => navigate(`/countries/${id}`)}
          orderBy={orderBy}
          handleOrder={setOrderBy}
          sortDirection={sortDirection}
          handleSortDirection={setSortDirection}
        />
      </Box>
      <ToastMessage toast={toast} close={() => setToast('')} messageType={toastType} />
    </>
  );
}

export default Countries;
